<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	//Hooks
	import { onMount, getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { onSettingsResponse, sleepingResponse } from 'src/helpers/device_settings_helpers';
	import { getWebHookTypes } from 'src/helpers/actions/webhooks/core/webhook_helpers';
	import {
		getWebhookList,
		getWebhookListSupported,
		parseWebHooksForAllChannels,
	} from 'src/helpers/actions/webhooks/supported/webhook_list_supported_helpers';
	import {
		isCamera,
		isPill,
		isShellyPresence,
		isWallDisplay,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { getAddonStatus, getPlusUniCountStatus } from 'src/helpers/addons/addon_helpers';
	import {
		getPeripheralDeviceStatus,
		isPeripheralDevice,
	} from 'src/helpers/addons/addon_device_helpers';
	import { SLEEPING_TYPES } from 'src/constants';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Alert, Button, Loader, Toggle } from '@Shelly/shelly-design-system';
	//Components
	import HooksListV2 from './HooksListV2.svelte';
	import CreateWebhooks from './CreateWebhooks.svelte';
	import NoPeripheralsAlert from 'src/components/ThePill/NoPeripheralsAlert.svelte';
	//Helpers
	import { getVirtualWebHookTypes } from 'src/helpers/actions/webhooks/core/webhook_virtual_helpers';
	import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type {
		WebHooksParams,
		WebHooksRequest,
		WebHookSupportedList,
	} from 'src/types/requests/settings/webhooks';
	import {
		bthomeButtonWebhookListSupported,
		bthomeWebhookListSupported,
		virtualWebhookListSupported,
	} from 'src/helpers/actions/webhooks/supported/webhook_list_supported';

	export let device: Device,
		peripheralsWebHook = false,
		plusUniWebHook = false,
		isOffline = false;

	export const refresh = () => {
		peripherals = initPeripherals();
		if (peripherals && actions.length) {
			getWebHooks();
		}
	};

	if (isPeripheralDevice(device.type) || isPill(device.type)) {
		peripheralsWebHook = true;
	}

	const getWebHooks = async () => {
		waitForResponse = true;
		webhookListError = '';
		actions = [];
		deviceActionsCount = 0;
		const res: WebHooksRequest = await gen2Request(device, 'webhook.list', {}, false);
		if (res?.data?.sleeping) {
			//updating status if device is sleeping, for now it's not
			const status = readDeviceStatus(device.id);
			status._sleeping = true;
			devices_status.update_device_status({ id: device.id, msg: status });
		}
		if (res?.isok && !res?.data?.sleeping) {
			requestData = res?.data?.hooks || res?.hooks || [];
			parseWebHooks();
		} else {
			requestData = null;
			webhookListError =
				sleepingResponse(res, device.type) ||
				$format('load_actions_err_toast', {
					default: "Could not load the web hooks' list.",
				});
		}
		waitForResponse = false;
	};

	const parseWebHooks = () => {
		actions = [];
		deviceActionsCount = 0;
		if (!requestData) return;
		const hooks = requestData,
			params: WebHooksParams[] = Object.values(hooks) as WebHooksParams[];
		deviceActionsCount = hooks?.length || 0;
		if (!peripherals || showActionForAllChannels) {
			const webhooks = getWebhookList(device, params, peripheralsWebHook);
			webhooks.forEach((webhook) => {
				actions.push({
					...getWebHookTypes(device, plusUniWebHook),
					...webhook,
					deviceName: device.name,
					source: 'device',
				});
			});
		}
		if (showActionForAllChannels) {
			actions = parseWebHooksForAllChannels(device, params, actions);
			const vGroupComponents: WebHooksParams['components'] =
				getVirtualWebHookTypes(device, true)?.components || [];
			params.forEach((webhook) => {
				const virtualComponent = vGroupComponents.find(
					(i) => i.cid === webhook.cid && webhook.event?.startsWith(`${i.type}.`),
				);
				if (virtualComponent && !actions.some((action) => action.id === webhook.id)) {
					actions.push({
						...virtualComponent,
						...webhook,
						components: vGroupComponents,
						source: 'virtual',
					});
				}
			});
		}
		if (peripherals || showActionForAllChannels) {
			const peripheralsWebhook = showActionForAllChannels
				? [...getAddonStatus(device), ...getPlusUniCountStatus(device)]
				: peripherals;
			peripheralsWebhook.forEach((el) => {
				params
					.filter(
						(i: WebHooksParams) =>
							i.cid == el.sensor.extid &&
							i.event.startsWith(`${el.sensor.type}.`) &&
							!actions.some((action) => action.id === i.id),
					)
					.forEach((webhook) => {
						actions.push({
							...el.sensor,
							...webhook,
							icon: el.icon,
							subtitle: el.sensor.name || el.sensor.label,
							source: 'peripheral',
						});
					});
			});
		}
	};

	const createAction = async (data: WebHooksParams = null) => {
		showWizard = false;
		if (data === null) {
			if (peripherals) {
				data = {
					inputType: peripherals[0].sensor.inputType,
					type: peripherals[0].sensor.type,
					cid: peripherals[0].sensor.extid,
					unit: peripherals[0].sensor.unit,
					icon: peripherals[0].sensor.icon,
					repeat_period: 0,
					name: '',
				};
			} else {
				data = {
					...getWebHookTypes(device),
					name: '',
					//Wall Display and sleeping devices do not support repeat period.
					repeat_period:
						isWallDisplay(device.type) || SLEEPING_TYPES.includes(device.type) ? null : 0,
				};
			}
		}
		selectedAction = data;
		if (data !== null && showActionForAllChannels) {
			supportedActionsList = {
				types: {
					...getWebhookListSupported(device).types,
					...virtualWebhookListSupported.types,
					...bthomeWebhookListSupported.types,
					...bthomeButtonWebhookListSupported.types,
				},
			};
		} else {
			supportedActionsList = getWebhookListSupported(device, peripheralsWebHook);
		}
		showWizard = true;
	};

	const deleteAction = async (data: WebHooksParams) => {
		const res: WebHooksRequest = await gen2Request(device, 'webhook.delete', {
			id: data.id,
			cid: device.channel,
		});
		if (res.isok) {
			requestData = requestData.filter((i) => i.id != data.id);
			parseWebHooks();
		}
		onSettingsResponse(res, device, {
			success: $format('action_deleted_scc_toast', {
				default: 'Action was deleted.',
			}),
			danger: $format('action_delete_failed_toast', {
				default: 'Failed to delete action!',
			}),
		});
	};

	const toggleAction = async (data: WebHooksParams) => {
		const res: WebHooksRequest = await gen2Request(
			device,
			'webhook.update',
			isWallDisplay(device.type) // Wall Display
				? data
				: {
						id: data.id,
						enable: data.enable,
					},
		);
		if (res?.isok) {
			const item = requestData.find((i) => i.id === data.id);
			if (item) item.enable = data.enable;
		}
		onSettingsResponse(res, device, {
			success: data.enable
				? $format('action_enabled_scc_toast', {
						default: 'Action is enabled.',
					})
				: $format('action_disabled_scc_toast', {
						default: 'Action is disabled.',
					}),
		});
	};
	const initPeripherals = () => {
		if (isPeripheralDevice(device.type)) {
			return getPeripheralDeviceStatus(device);
		}
		if (plusUniWebHook) {
			return getPlusUniCountStatus(device);
		}
		return peripheralsWebHook ? getAddonStatus(device) : null;
	};

	const showActionForAllChannelsOption = (requestData) => {
		const channelActions = requestData?.filter((i) => !i.event?.startsWith('lnm.'));
		if (isXT1Module(device?.type) && channelActions?.length) {
			return (
				device?.channels_count > 1 ||
				getWebhookList(device, channelActions, false).length < channelActions.length
			);
		}
		return (
			channelActions?.length &&
			device?.channel < 100 &&
			!plusUniWebHook &&
			!peripheralsWebHook &&
			!isCamera(device?.type) &&
			!isShellyPresence(device?.type) &&
			!isXT1Module(device?.type) &&
			[
				...channelActions.filter((i) => i.cid >= 100),
				...parseWebHooksForAllChannels(device, channelActions, []),
			].length > 0
		);
	};

	let peripherals = initPeripherals(),
		actions: WebHooksParams[] = [],
		deviceActionsCount = 0,
		showWizard = false,
		supportedActionsList: WebHookSupportedList = null,
		selectedAction: WebHooksParams = null,
		webhookListError = '',
		waitForResponse = false,
		showActionForAllChannels = false,
		requestData = null;

	onMount(() => {
		if (!isOffline) getWebHooks();
	});

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && !waitForResponse && peripherals?.length !== 0 && !webhookListError) {
			stickyBottomAction.set({
				onClick: () => createAction(),
				loading: false,
				disabled: deviceActionsCount >= 20,
				label: $format('button_add_action', { default: 'Add action' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if waitForResponse}
	<div class="d-flex justify-content-center">
		<Loader />
	</div>
{:else if peripherals?.length === 0}
	<NoPeripheralsAlert {device} />
{:else if webhookListError}
	<Alert type="danger" heading={webhookListError} class="mb-s" />
	<Button
		size="m"
		class="width-full mr-s text-capitalized"
		responsiveSize={{
			sm: 'm',
		}}
		variant="primary"
		on:click={getWebHooks}
	>
		{$format('button_rescan', { default: 'Rescan' })}
	</Button>
{:else}
	{#if deviceActionsCount >= 20}
		<Alert
			type="info"
			isHeadingBold
			heading={$format('actions_limit_alert_title', {
				default: 'Actions limit reached!',
			})}
			class="mb-s"
			message={$format('actions_limit_alert_message', {
				default: 'You have reached the limit of 20 actions for this device.',
			})}
		/>
	{/if}
	{#if showActionForAllChannelsOption(requestData)}
		<Toggle
			class="mb-s ml-s"
			size="m"
			checked={showActionForAllChannels}
			on:click={() => {
				showActionForAllChannels = !showActionForAllChannels;
				parseWebHooks();
			}}
			id="toggle-q9jskua"
			>{$format('actions_show_all_device_channels', {
				default: 'Show actions for all device channels',
			})}</Toggle
		>
	{/if}
	{#if !actions.length}
		<Alert
			heading={$format('no_active_actions', {
				default: 'No actions registered',
			})}
			isHeadingBold
			message={$format('first_add_action', {
				default: 'If you want to use this feature,  you first need to add an action.',
			})}
		/>
	{:else}
		<HooksListV2
			list={actions}
			text={$format('no_active_actions', {
				default: 'No actions registered.',
			})}
			removeLastSpacing
			{showActionForAllChannels}
			on:edit={(e) => createAction(e.detail)}
			on:delete={(e) => deleteAction(e.detail)}
			on:toggle={(e) => {
				toggleAction(e.detail);
			}}
		/>
	{/if}

	{#if !$showNewLayout}
		<div class="pt-s">
			<Button
				size="m"
				width="full"
				disabled={deviceActionsCount >= 20}
				on:click={() => createAction()}
			>
				{$format('button_add_action', {
					default: 'Add action',
				})}
			</Button>
		</div>
	{/if}
{/if}

{#if showWizard}
	<CreateWebhooks
		peripherals={plusUniWebHook ? null : peripherals}
		{device}
		actionProps={selectedAction}
		{supportedActionsList}
		bind:showWizard
		on:save={getWebHooks}
	/>
{/if}
